import { useDispatch } from "react-redux"
import { Gallery } from "../../../types/galleryData";
import { useSelector } from "react-redux";
import { AppState } from "../../../store/root.reducer";
import React from "react";
import { DeleteGallery, GetGalleries } from "../store/admin.actions";
import { useNavigate } from "react-router-dom";
import Button from "../../../components/molecules/Button";
import { ConfirmModal } from "../../../components/modals/confirm-modal";
import { ApiClient } from "../../../utils/apiClient";
import { toast } from "react-toastify";
import { updateGalleryRequest } from "../store/admin.api";
import styles from "../admin-theme.module.css";

const DEFAULT_GALLERY_ARTWORK_LOAD_QUOTA_MB = 200;
const MAX_GALLERY_ARTWORK_LOAD_QUOTA_MB = 10240;

const resolveGalleryArtworkLoadQuotaMb = (gallery: Gallery): number => {
    const quota = Number(gallery.artworkLoadQuotaMb);
    return Number.isFinite(quota) && quota > 0 ? quota : DEFAULT_GALLERY_ARTWORK_LOAD_QUOTA_MB;
};

export const GalleriesList = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const galleries: Gallery[] = useSelector((state: AppState) => state.admin.galleries);

    const [ nonMom, setNonMom ] = React.useState<boolean>(false);
    const [ nonMomGalleries, setNonMomGalleries ] = React.useState<Gallery[]>([]);
    const [ showConfirmDelete, setShowConfirmDelete ] = React.useState<boolean>(false);
    const [ galleryIdToDelete, setGalleryIdToDelete ] = React.useState<string | null>(null);
    const [ quotaDrafts, setQuotaDrafts ] = React.useState<Record<string, string>>({});
    const [ savingQuotaByGallery, setSavingQuotaByGallery ] = React.useState<Record<string, boolean>>({});

    const BASE_API = process.env.REACT_APP_BASE_API as string;
    const apiClient = React.useMemo(() => ApiClient(BASE_API), [BASE_API]);

    React.useEffect(() => {
        (async () => {
            dispatch(GetGalleries());
        })();
    }, []);

    React.useEffect(() => {
        setQuotaDrafts((current) => {
            const next = { ...current };
            let changed = false;
            galleries.forEach((gallery) => {
                if (gallery?._id && next[gallery._id] === undefined) {
                    next[gallery._id] = String(resolveGalleryArtworkLoadQuotaMb(gallery));
                    changed = true;
                }
            });
            return changed ? next : current;
        });
    }, [galleries]);

    if (galleries.length === 0) {
        return (
            <div className="container">
                No galleries
            </div>
        )
    }

    const createGallery = () => {
        navigate(`/admin/galleries/new`);
    }

    const editGallery = (id: string) => {
        // navigate to the personal gallery page...
        navigate(`/gallery/${id}?t=personal`);
    }

    const editGalleryMood = (id: string) => {
        navigate(`/gallery/${id}?t=admin`);
    }

    const deleteGallery = (id: string) => {
        setGalleryIdToDelete(id);
        setShowConfirmDelete(true);
    }

    const saveGalleryQuota = async (gallery: Gallery) => {
        const rawQuota = quotaDrafts[gallery._id] ?? String(resolveGalleryArtworkLoadQuotaMb(gallery));
        const quota = Number(rawQuota);
        if (!Number.isFinite(quota) || quota <= 0 || quota > MAX_GALLERY_ARTWORK_LOAD_QUOTA_MB) {
            toast.error(`Quota must be between 1 and ${MAX_GALLERY_ARTWORK_LOAD_QUOTA_MB} MB`, { position: "bottom-left" });
            return;
        }

        setSavingQuotaByGallery((current) => ({ ...current, [gallery._id]: true }));
        try {
            const { data } = await updateGalleryRequest({
                _id: gallery._id,
                artworkLoadQuotaMb: quota,
            } as any);

            if (data?.status) {
                const savedQuota = data.gallery?.artworkLoadQuotaMb ?? quota;
                setQuotaDrafts((current) => ({ ...current, [gallery._id]: String(savedQuota) }));
                dispatch(GetGalleries());
                toast.success("Artwork load quota updated", { position: "bottom-left" });
            } else {
                toast.error(data?.msg || "Failed to update artwork load quota", { position: "bottom-left" });
            }
        } catch (error: any) {
            toast.error(error?.message || "Failed to update artwork load quota", { position: "bottom-left" });
        } finally {
            setSavingQuotaByGallery((current) => ({ ...current, [gallery._id]: false }));
        }
    }

    return (
        <div className="container admin-galleries">
            <div className="mt-3">
                <div className="d-flex justify-content-between mb-3">
                    <Button className="btn small"
                        onClick={createGallery}
                    >Create</Button>
                    <div className="mt-3">
                        <input type="checkbox" id="Non-mom" className="me-1" checked={nonMom} onChange={e => setNonMom(e.target.checked)}/>
                        <label htmlFor="Non-mom">Non mom galleries?</label>
                    </div>
                </div>
            </div>
            <div className={`mt-3 ${styles.adminGalleriesScroller}`}>
            <table className={`table text-white table-striped table-hover mb-0 ${styles.adminGalleriesTable}`}>
                <thead>
                    <tr key={-1}>
                        <th>No.</th>
                        <th>Gallery</th>
                        <th>Owner</th>
                        <th>Space</th>
                        <th>Slot</th>
                        <th>Load Quota</th>
                        <th>Action</th>
                    </tr>
                </thead>
                <tbody>
                    {galleries.filter(g => nonMom ? (g.spaceType !== 'mom' && g.spaceType !== 'sellermom') : true).map((gallery: any, i) => (
                        <tr key={gallery?._id ?? i}>
                            <td>{i + 1}</td>
                            <td className={styles.adminGalleryIdentity}>
                                <strong>{gallery.data?.title || gallery.name || "Untitled"}</strong>
                                <span>{gallery._id}</span>
                            </td>
                            <td className={styles.adminGalleryOwner}>
                                <strong>{gallery.user?.name || "System"}</strong>
                                <span>{gallery.user?.email || "Template"}</span>
                            </td>
                            <td>{gallery.spaceType}</td>
                            <td>
                                {(gallery.spaceType === 'mom' || gallery.spaceType === 'sellermom') ? (
                                    gallery.data.slotTarget
                                ) : (
                                    <select
                                        className={`form-select form-select-sm ${styles.lightSelect} ${styles.adminGallerySlotSelect}`}
                                        defaultValue={`${gallery.data.slotTarget}`}
                                        disabled={!gallery.data?.published}
                                        title={!gallery.data?.published ? "Publish gallery to change its diorama slot" : undefined}
                                        aria-label={`Diorama slot for ${gallery.data?.title || gallery._id}`}
                                        onChange={async (e) => {
                                            const newSlot = parseInt(e.target.value, 10);
                                            const res = await apiClient.changeDioramaSlot(gallery._id, newSlot);
                                            if (res?.status) {
                                                dispatch(GetGalleries());
                                                toast.success("Slot updated successfully", { position: "bottom-left" });
                                            } else {
                                                toast.error("Failed to update slot", { position: "bottom-left" });
                                            }
                                        }}
                                    >
                                        {[...Array(9)].map((_, idx) => {
                                            const num = idx + 1;
                                            return (
                                                <option key={num} value={num}>{num}</option>
                                            );
                                        })}
                                    </select>
                                )}
                            </td>
                            <td>
                                <div className={styles.adminGalleryQuotaControl}>
                                    <input
                                        type="number"
                                        min={1}
                                        max={MAX_GALLERY_ARTWORK_LOAD_QUOTA_MB}
                                        step={1}
                                        className={`form-control form-control-sm ${styles.lightInput}`}
                                        value={quotaDrafts[gallery._id] ?? String(resolveGalleryArtworkLoadQuotaMb(gallery))}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setQuotaDrafts((current) => ({ ...current, [gallery._id]: value }));
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                void saveGalleryQuota(gallery);
                                            }
                                        }}
                                        aria-label={`Artwork load quota for ${gallery.data?.title || gallery._id}`}
                                    />
                                    <Button
                                        className="btn small"
                                        disabled={savingQuotaByGallery[gallery._id]}
                                        onClick={() => saveGalleryQuota(gallery)}
                                    >Save</Button>
                                </div>
                            </td>
                            <td>
                                <div className={styles.adminGalleryActions}>
                                    <Button
                                        className="btn small"
                                        onClick={() => editGallery(gallery._id)}
                                    >Edit</Button>
                                    <Button
                                        className="btn small"
                                        onClick={() => editGalleryMood(gallery._id)}
                                    >Edit Mood</Button>
                                    <Button
                                        className="btn small"
                                        onClick={() => deleteGallery(gallery._id)}
                                    >Delete</Button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            </div>

            <ConfirmModal
                show={showConfirmDelete}
                onHide={() => {
                    setShowConfirmDelete(false);
                    setGalleryIdToDelete(null);
                }}
                onConfirm={() => {
                    if (galleryIdToDelete) {
                        console.log("galleryIdToDelete", galleryIdToDelete);
                        dispatch(DeleteGallery(galleryIdToDelete));
                    }
                    setShowConfirmDelete(false);
                    setGalleryIdToDelete(null);
                }}
                title="Delete gallery?"
                message="Are you sure you want to delete this gallery? This action cannot be undone."
                confirmText="Delete"
                cancelText="Cancel"
            />
        </div>
    )
}
